import { gql } from "@apollo/client";
import { Database, Q } from "@nozbe/watermelondb";
import {
  LayoutDeepNestingFragment,
  LayoutNodeDeepNestingFragment,
  LayoutNodeLinkDeepNestingFragment,
} from "@towersystems/roam-common/lib/generated-graphql-fragment";
import {
  Layout as LayoutEntity,
  LayoutNodeInput,
} from "@towersystems/roam-common/lib/generated-types";
import { client } from "../../../apollo";
import LayoutNodeModel from "../../data/model/LayoutNode";
import { default as LayoutModel } from "../../data/model/Layout";
import { AsyncLayoutActionDefinition } from "../types";
import { LayoutNodeFilterDeepNestingFragment } from "@towersystems/roam-common/lib/generated-graphql-fragment";

export const applyDefaultLayoutConfigurationMutation = gql`
  ${LayoutNodeFilterDeepNestingFragment}
  ${LayoutNodeLinkDeepNestingFragment}
  ${LayoutNodeDeepNestingFragment}
  ${LayoutDeepNestingFragment}
  mutation applyDefaultLayoutConfiguration($id: ID) {
    applyDefaultLayoutConfiguration(id: $id) {
      ...LayoutDeepNesting
    }
  }
`;

export const applyDefaultLayoutDefinition = new AsyncLayoutActionDefinition<
  LayoutEntity,
  LayoutModel,
  (
    database: Database,
    layoutId: string,
    nodesToCreate: LayoutNodeInput[]
  ) => Promise<LayoutModel>,
  (id: string) => Promise<LayoutEntity>
>({
  localAction: async (
    database: Database,
    layoutId: string,
    nodesToCreate: LayoutNodeInput[]
  ) => {
    await database.write(async () => {
      const layoutNodes = await database.collections
        .get<LayoutNodeModel>("layout_nodes")
        .query(Q.where("layout_id", layoutId))
        .fetch();

      const deletedLayoutNodes = layoutNodes.map((node) =>
        node.prepareDestroyPermanently()
      );

      return database.batch(
        ...deletedLayoutNodes,
        ...nodesToCreate.map((layoutKeyInput) =>
          database.collections
            .get<LayoutNodeModel>("layout_nodes")
            .prepareCreate((layoutNode) => {
              layoutNode._raw.id = layoutKeyInput.id || "";
              layoutNode.layout.id = layoutId;
              layoutNode.label = layoutKeyInput.label || null;
              layoutNode.color = layoutKeyInput.color;
              layoutNode.showImage = layoutKeyInput.showImage || false;
              layoutNode.productId = layoutKeyInput.productId || null;
              layoutNode.type = layoutKeyInput.type || null;
              layoutNode.position = layoutKeyInput.position || 0;
              layoutNode.action = layoutKeyInput.action || null;
              layoutNode.link = layoutKeyInput.link || null;
            })
        )
      );
    });

    return database.collections.get<LayoutModel>("layouts").find(layoutId);
  },
  remoteAction: async (id?: string) => {
    return client
      .mutate({
        variables: { id },
        mutation: applyDefaultLayoutConfigurationMutation,
      })
      .then((result) => {
        return result.data?.applyDefaultLayoutConfiguration;
      });
  },
});
